// src/components/KnowledgeDashboard.js
// Knowledge Health Dashboard — ROI 1 v3
// D1: EntityLink real (lazy import QuickViews)
// D2: Edição reconhecida como ROI 1.1 (TODO documentado)
// D3: verifiedBy via useAuth (hook)
// D4: Error banner visível
// D5: Migration versionada (arquivo separado)
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ChevronDown, ChevronRight,
         Check, AlertTriangle, Archive, RefreshCw, AlertCircle } from 'lucide-react';
import useKnowledgeAtoms from '../hooks/useKnowledgeAtoms';
import EntityLink from './shared/EntityLink';

// ── Constantes de UI ─────────────────────────────────────────
const CATEGORY_META = {
  contact:     { icon: '👤', label: 'Contato', color: 'bg-blue-100 text-blue-800' },
  corporate:   { icon: '🏢', label: 'Corporativo', color: 'bg-indigo-100 text-indigo-800' },
  negotiation: { icon: '🤝', label: 'Negociação', color: 'bg-amber-100 text-amber-800' },
  ownership:   { icon: '📋', label: 'Propriedade', color: 'bg-green-100 text-green-800' },
  market:      { icon: '📊', label: 'Mercado', color: 'bg-purple-100 text-purple-800' },
  family:      { icon: '👨‍👩‍👧', label: 'Família', color: 'bg-rose-100 text-rose-800' },
  encumbrance: { icon: '⚠️', label: 'Ônus', color: 'bg-orange-100 text-orange-800' },
  financial:   { icon: '💰', label: 'Financeiro', color: 'bg-emerald-100 text-emerald-800' },
  geographic:  { icon: '📍', label: 'Geográfico', color: 'bg-teal-100 text-teal-800' },
  legal:       { icon: '⚖️', label: 'Legal', color: 'bg-slate-100 text-slate-800' },
};

const CLASS_META = {
  fact:      { label: 'Fato', color: 'bg-green-100 text-green-700', icon: '✓' },
  inference: { label: 'Inferência', color: 'bg-yellow-100 text-yellow-700', icon: '?' },
  action:    { label: 'Ação', color: 'bg-blue-100 text-blue-700', icon: '→' },
};

const SOURCE_META = {
  api:      { label: 'API/Apollo', color: 'text-violet-600' },
  human:    { label: 'Humano', color: 'text-green-600' },
  database: { label: 'Database', color: 'text-blue-600' },
  document: { label: 'Documento', color: 'text-amber-600' },
};

const CONFIDENCE_BANDS = [
  { key: null, label: 'Todas' },
  { key: 'high_95+', label: '≥95% Alta' },
  { key: 'medium_80+', label: '80-94%' },
  { key: 'low_60+', label: '60-79%' },
  { key: 'very_low', label: '<60%' },
];

const ENTITY_TYPES = [
  { key: null, label: 'Todas' },
  { key: 'empresa', label: '🏢 Empresa' },
  { key: 'contato', label: '👤 Contato' },
  { key: 'imovel', label: '🏠 Imóvel' },
  { key: 'oferta', label: '📄 Oferta' },
];

const STATUS_OPTIONS = [
  { key: 'active', label: 'Ativos' },
  { key: null, label: 'Todos' },
  { key: 'disputed', label: 'Disputados' },
  { key: 'superseded', label: 'Superados' },
  { key: 'archived', label: 'Arquivados' },
];

// ── Toast ────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type === 'success' ? 'bg-green-600' : 'bg-red-600';
  return (
    <div className={`fixed bottom-6 right-6 z-[9999] ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium`}>
      {toast.type === 'success' ? '✓' : '✗'} {toast.message}
    </div>
  );
}

// ── D4 FIX: Error Banner ─────────────────────────────────────
function ErrorBanner({ error, onRetry }) {
  if (!error) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3">
      <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-medium text-red-800">Erro ao carregar dados</p>
        <p className="text-xs text-red-600 mt-0.5">{error}</p>
      </div>
      <button onClick={onRetry} className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 border border-red-200">
        Tentar novamente
      </button>
    </div>
  );
}

// ── Metric Card ──────────────────────────────────────────────
function MetricCard({ label, value, detail, alert }) {
  return (
    <div className={`px-4 py-3 rounded-lg border ${alert ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className="text-2xl font-bold text-slate-800">{value ?? '—'}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      {detail && <div className="text-[10px] text-amber-600 font-medium mt-1">{detail}</div>}
    </div>
  );
}

// ── Filter Chip ──────────────────────────────────────────────
function FilterChip({ active, onClick, children, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
        active
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
      }`}
    >
      {children}
      {count != null && <span className="ml-1 opacity-70">({count})</span>}
    </button>
  );
}

// ── Confirmation Dialog ──────────────────────────────────────
function ConfirmDialog({ open, title, message, onConfirm, onCancel, variant = 'default' }) {
  if (!open) return null;
  const btnColor = variant === 'danger'
    ? 'bg-red-600 hover:bg-red-700'
    : variant === 'warning'
    ? 'bg-amber-600 hover:bg-amber-700'
    : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/30" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-xl p-5 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-600 mt-2">{message}</p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50">Cancelar</button>
          <button onClick={onConfirm} className={`px-3 py-1.5 text-sm text-white rounded-lg ${btnColor}`}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}

// ── D1 FIX: Atom Row com EntityLink real ─────────────────────
function AtomRow({ atom, entityName, onVerify, onDispute, onArchive, expanded, onToggle }) {
  const cat = CATEGORY_META[atom.category] || { icon: '📄', label: atom.category, color: 'bg-slate-100' };
  const cls = CLASS_META[atom.knowledge_class] || CLASS_META.fact;
  const src = SOURCE_META[atom.source_type] || { label: atom.source_type, color: 'text-slate-500' };
  const confPct = atom.confidence != null ? Math.round(atom.confidence * 100) : null;
  const confColor = confPct >= 95 ? 'text-green-600' : confPct >= 80 ? 'text-blue-600' : confPct >= 60 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
      {/* Linha principal */}
      <div className="flex items-start gap-2 px-4 py-2.5 cursor-pointer" onClick={onToggle}>
        <button className="mt-0.5 text-slate-400 hover:text-slate-600 flex-shrink-0">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <span className={`px-1.5 py-0.5 text-[10px] rounded flex-shrink-0 ${cat.color}`}>
          {cat.icon} {cat.label}
        </span>
        <span className={`px-1.5 py-0.5 text-[10px] rounded flex-shrink-0 ${cls.color}`}>
          {cls.icon} {cls.label}
        </span>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 leading-snug">{atom.content}</p>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400 flex-wrap">
            {/* D1 FIX: EntityLink real em vez de <span> */}
            {entityName && atom.entity_type && atom.entity_id && (
              <EntityLink
                entityType={atom.entity_type}
                entityId={atom.entity_id}
                label={entityName}
                className="text-[10px]"
              />
            )}
            {entityName && !atom.entity_type && (
              <span className="font-medium text-slate-500">{entityName}</span>
            )}
            <span className={src.color}>{src.label}</span>
            {confPct != null && <span className={`font-medium ${confColor}`}>{confPct}%</span>}
            {atom.verified_by && <span className="text-green-600">✓ {atom.verified_by}</span>}
            {atom.subcategory && <span>{atom.subcategory}</span>}
          </div>
        </div>

        {atom.status === 'disputed' && (
          <span className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded flex-shrink-0">Disputado</span>
        )}
      </div>

      {/* Detalhes expandidos */}
      {expanded && (
        <div className="px-10 pb-3 space-y-2">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-slate-500">
            <div><span className="font-medium">Tipo:</span> {atom.atom_type}</div>
            <div><span className="font-medium">Extração:</span> {atom.extraction_method || '—'}</div>
            {atom.extraction_model && <div><span className="font-medium">Modelo:</span> {atom.extraction_model}</div>}
            {atom.created_by_agent && <div><span className="font-medium">Agente:</span> {atom.created_by_agent}</div>}
            {atom.valid_from && <div><span className="font-medium">Válido de:</span> {atom.valid_from}</div>}
            {atom.valid_to && <div><span className="font-medium">Válido até:</span> {atom.valid_to}</div>}
            {atom.reference_date && <div><span className="font-medium">Referência:</span> {atom.reference_date}</div>}
            <div><span className="font-medium">Criado:</span> {new Date(atom.created_at).toLocaleDateString('pt-BR')}</div>
          </div>

          {atom.source_excerpt && (
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 border-l-2 border-slate-300">
              <span className="font-medium">Trecho fonte:</span> {atom.source_excerpt}
            </div>
          )}

          {atom.structured_data && Object.keys(atom.structured_data).length > 0 && (
            <details className="text-[11px]">
              <summary className="text-slate-500 cursor-pointer hover:text-slate-700">Dados estruturados</summary>
              <pre className="mt-1 p-2 bg-slate-50 rounded text-[10px] text-slate-600 overflow-x-auto">
                {JSON.stringify(atom.structured_data, null, 2)}
              </pre>
            </details>
          )}

          {/* D2: Ações — Validar/Disputar/Arquivar. Edição = ROI 1.1 (TODO) */}
          <div className="flex items-center gap-2 pt-1">
            {!atom.verified_by && (
              <button
                onClick={(e) => { e.stopPropagation(); onVerify(atom.id); }}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-green-50 text-green-700 rounded border border-green-200 hover:bg-green-100"
              >
                <Check size={12} /> Validar
              </button>
            )}
            {atom.status !== 'disputed' && (
              <button
                onClick={(e) => { e.stopPropagation(); onDispute(atom.id); }}
                className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-amber-50 text-amber-700 rounded border border-amber-200 hover:bg-amber-100"
              >
                <AlertTriangle size={12} /> Disputar
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onArchive(atom.id); }}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-slate-50 text-slate-600 rounded border border-slate-200 hover:bg-slate-100"
            >
              <Archive size={12} /> Arquivar
            </button>
            {/* TODO ROI 1.1: botão Editar → modal com campos content, category, confidence, valid_from/to + audit trail */}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Componente Principal ─────────────────────────────────────
export default function KnowledgeDashboard() {
  const navigate = useNavigate();
  const {
    atoms, health, loading, error, toast, filters, setFilter,
    entityNames, nextPage, prevPage, hasMore,
    verifyAtom, disputeAtom, archiveAtom, refetch,
  } = useKnowledgeAtoms();

  const [expandedAtomId, setExpandedAtomId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef(null);

  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', action: null, variant: 'default' });

  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, []);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { setFilter('search', val); }, 400);
  };

  const handleArchive = (atomId) => {
    setConfirm({
      open: true, title: 'Arquivar átomo?',
      message: 'O átomo será removido da lista ativa. Restaurável via filtro "Arquivados".',
      variant: 'danger',
      action: () => { archiveAtom(atomId); setConfirm(c => ({ ...c, open: false })); },
    });
  };

  const handleDispute = (atomId) => {
    setConfirm({
      open: true, title: 'Disputar átomo?',
      message: 'O átomo será marcado como disputado — informação possivelmente incorreta.',
      variant: 'warning',
      action: () => { disputeAtom(atomId); setConfirm(c => ({ ...c, open: false })); },
    });
  };

  const categoryList = useMemo(() => {
    if (!health?.by_category) return [];
    return Object.entries(health.by_category).sort(([, a], [, b]) => b - a);
  }, [health]);

  const sourceList = useMemo(() => {
    if (!health?.by_source) return [];
    return Object.entries(health.by_source).sort(([, a], [, b]) => b - a);
  }, [health]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Toast toast={toast} />
      <ConfirmDialog
        open={confirm.open} title={confirm.title} message={confirm.message}
        variant={confirm.variant} onConfirm={confirm.action}
        onCancel={() => setConfirm(c => ({ ...c, open: false }))}
      />

      {/* ── Header ──────────────────────────────────── */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="w-full px-4 lg:px-6 2xl:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  🧠 Knowledge Health Dashboard
                </h1>
                <p className="text-xs text-slate-500">
                  {health?.total_atoms || 0} átomos · {health?.entidades_cobertas || 0} entidades · {health?.categorias || 0} categorias
                </p>
              </div>
            </div>
            <button onClick={refetch}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200">
              <RefreshCw size={14} /> Atualizar
            </button>
          </div>
        </div>
      </div>

      <div className="w-full px-4 lg:px-6 2xl:px-8 py-5 space-y-5">

        {/* D4 FIX: Error banner */}
        <ErrorBanner error={error} onRetry={refetch} />

        {/* ── Métricas ──────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <MetricCard label="Total" value={health?.total_atoms} />
          <MetricCard label="Ativos" value={health?.active} />
          <MetricCard label="Fatos" value={health?.by_class?.fact || 0} />
          <MetricCard label="Inferências" value={health?.by_class?.inference || 0}
            alert={health?.inference_sem_evidencia > 0}
            detail={health?.inference_sem_evidencia > 0 ? `⚠ ${health.inference_sem_evidencia} sem evidência` : null} />
          <MetricCard label="Disputados" value={health?.disputed || 0} alert={health?.disputed > 0} />
          <MetricCard label="Sem fonte" value={health?.sem_source || 0} alert={health?.sem_source > 0} />
          <MetricCard label="Entidades" value={health?.entidades_cobertas} />
        </div>

        {/* ── Barra de confiança ────────────────────── */}
        {health?.by_confidence && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Distribuição por Confiança</h3>
            <div className="flex gap-1 h-6 rounded overflow-hidden">
              {['high_95+', 'medium_80+', 'low_60+', 'very_low'].map(band => {
                const count = health.by_confidence[band] || 0;
                const pct = health.total_atoms > 0 ? (count / health.total_atoms) * 100 : 0;
                const colors = { 'high_95+': 'bg-green-500', 'medium_80+': 'bg-blue-500', 'low_60+': 'bg-amber-500', 'very_low': 'bg-red-500' };
                if (pct === 0) return null;
                return (
                  <div key={band}
                    className={`${colors[band]} flex items-center justify-center text-white text-[10px] font-medium cursor-pointer hover:opacity-80 transition-opacity`}
                    style={{ width: `${Math.max(pct, 3)}%` }}
                    onClick={() => setFilter('confidence_band', filters.confidence_band === band ? null : band)}
                    title={`${band}: ${count} átomos (${pct.toFixed(1)}%)`}>
                    {count}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-2 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> ≥95% ({health.by_confidence['high_95+'] || 0})</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> 80-94% ({health.by_confidence['medium_80+'] || 0})</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> 60-79% ({health.by_confidence['low_60+'] || 0})</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> &lt;60% ({health.by_confidence['very_low'] || 0})</span>
            </div>
          </div>
        )}

        {/* ── Filtros ──────────────────────────────── */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={searchInput} onChange={handleSearch}
              placeholder="Buscar átomos por conteúdo..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none" />
          </div>

          <div>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Categoria</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <FilterChip active={!filters.category} onClick={() => setFilter('category', null)}>Todas</FilterChip>
              {categoryList.map(([cat, count]) => (
                <FilterChip key={cat} active={filters.category === cat}
                  onClick={() => setFilter('category', filters.category === cat ? null : cat)} count={count}>
                  {CATEGORY_META[cat]?.icon || '📄'} {CATEGORY_META[cat]?.label || cat}
                </FilterChip>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Fonte</span>
              <div className="flex flex-wrap gap-1 mt-1">
                <FilterChip active={!filters.source_type} onClick={() => setFilter('source_type', null)}>Todas</FilterChip>
                {sourceList.map(([src, count]) => (
                  <FilterChip key={src} active={filters.source_type === src}
                    onClick={() => setFilter('source_type', filters.source_type === src ? null : src)} count={count}>
                    {SOURCE_META[src]?.label || src}
                  </FilterChip>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Classe</span>
              <div className="flex flex-wrap gap-1 mt-1">
                <FilterChip active={!filters.knowledge_class} onClick={() => setFilter('knowledge_class', null)}>Todas</FilterChip>
                {Object.entries(CLASS_META).map(([key, meta]) => (
                  <FilterChip key={key} active={filters.knowledge_class === key}
                    onClick={() => setFilter('knowledge_class', filters.knowledge_class === key ? null : key)}>
                    {meta.icon} {meta.label}
                  </FilterChip>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Confiança</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {CONFIDENCE_BANDS.map(band => (
                  <FilterChip key={band.key || 'all'} active={filters.confidence_band === band.key}
                    onClick={() => setFilter('confidence_band', band.key)}>
                    {band.label}
                  </FilterChip>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Entidade</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {ENTITY_TYPES.map(et => (
                  <FilterChip key={et.key || 'all'} active={filters.entity_type === et.key}
                    onClick={() => setFilter('entity_type', et.key)}>
                    {et.label}
                  </FilterChip>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Status</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {STATUS_OPTIONS.map(st => (
                  <FilterChip key={st.key || 'all'} active={filters.status === st.key}
                    onClick={() => setFilter('status', st.key)}>
                    {st.label}
                  </FilterChip>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Lista de Átomos ──────────────────────── */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">
              {loading ? 'Carregando...' : `${atoms.length} átomos${hasMore ? '+' : ''} · Página ${filters.page + 1}`}
            </span>
            <div className="flex gap-2">
              <button onClick={prevPage} disabled={filters.page === 0}
                className="px-2 py-1 text-[11px] border rounded disabled:opacity-30 hover:bg-slate-100">← Anterior</button>
              <button onClick={nextPage} disabled={!hasMore}
                className="px-2 py-1 text-[11px] border rounded disabled:opacity-30 hover:bg-slate-100">Próxima →</button>
            </div>
          </div>

          {loading ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              <RefreshCw size={20} className="mx-auto mb-2 animate-spin" /> Carregando átomos...
            </div>
          ) : atoms.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              Nenhum átomo encontrado com os filtros atuais.
            </div>
          ) : (
            atoms.map(atom => (
              <AtomRow key={atom.id} atom={atom}
                entityName={entityNames[atom.entity_id] || null}
                expanded={expandedAtomId === atom.id}
                onToggle={() => setExpandedAtomId(expandedAtomId === atom.id ? null : atom.id)}
                onVerify={verifyAtom}
                onDispute={handleDispute}
                onArchive={handleArchive} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
